import {
  Filter,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import {
  type gqlV2,
  useDashboard,
  useInfiniteQuery,
  useQuery,
  useTeam,
} from '@vrplatform/data';
import {
  parseAsString,
  parseAsArrayOf,
  useQueryStates,
} from '@vrplatform/query-params';
import { day, hasValue } from '@vrplatform/utils';
import { Group } from '@mantine/core';
import type { syncStatus_enum } from '@vrplatform/graphql';
import {
  getListingName,
  whereConnectionStatusDefault,
  whereListings,
} from '@vrplatform/ui-common';
import { useMemo, useState } from 'react';
import { useGenericTableStore } from '../../stores';

export const usePaymentFilter = () => {
  const [queryFilter, setQueryFilter] = useQueryStates({
    search: parseAsString,
    listingIds: parseAsArrayOf(parseAsString),
    connectionId: parseAsString,
    listingCollectionId: parseAsString,
    appId: parseAsString,
    date: parseAsString,
    lineTypes: parseAsArrayOf(parseAsString),
    amount: parseAsString,
    automationResult: parseAsString,
  });

  const filterType = useGenericTableStore((s) => s.filterType);
  const stateFilter = useGenericTableStore((s) => s.filter);
  const setStateFilter = useGenericTableStore((s) => s.setFilter);

  const filter = useMemo(() => {
    if (filterType === 'query-params') {
      return queryFilter;
    }
    return stateFilter as typeof queryFilter;
  }, [filterType, stateFilter, queryFilter]);

  return {
    filter,
    setFilter: (filter: Partial<typeof stateFilter>) => {
      if (filterType === 'query-params') {
        setQueryFilter(filter);
      } else {
        setStateFilter(filter);
      }
    },
    reset: () => {
      const initial: Record<keyof typeof queryFilter, undefined> = {
        search: undefined,
        listingIds: undefined,
        listingCollectionId: undefined,
        connectionId: undefined,
        appId: undefined,
        date: undefined,
        lineTypes: undefined,
        amount: undefined,
        automationResult: undefined,
      };

      setStateFilter(initial);
      setQueryFilter(null);
    },
  };
};

export const PaymentFilter = () => {
  return (
    <Group>
      <SearchFilter />
      <DateFilter />
      <AmountFilter />
      <ConnectionFilter />
      <AppFilter />
      <LineTypeFilter />
      <ListingGroupFilter />
      <ListingFilter />
      <AutomationFilter />
    </Group>
  );
};

const SearchFilter = () => {
  const { filter, setFilter } = usePaymentFilter();

  return (
    <Filter.Search
      value={filter.search || ''}
      setValue={(v) => setFilter({ search: v || null })}
    />
  );
};

const DateFilter = () => {
  const { filter, setFilter } = usePaymentFilter();

  return (
    <Filter.Date
      value={filter.date || undefined}
      setValue={(value) => {
        setFilter({
          date:
            value
              ?.filter(hasValue)
              .map((date) => day(date).yyyymmdd())
              .join('...') || null,
        });
      }}
    />
  );
};

const AmountFilter = () => {
  const { filter, setFilter } = usePaymentFilter();

  return (
    <Filter.Amount
      value={filter.amount as any}
      setValue={(amount) => setFilter({ amount: amount?.text || null })}
    />
  );
};

const ConnectionFilter = () => {
  const [{ id: teamId }] = useTeam();
  const { filter, setFilter } = usePaymentFilter();
  const gate = useSelectQueryGate({ value: filter.connectionId });

  const [search, setSearch] = useState('');

  const queryData = useInfiniteQuery(
    (q, { teamId, search }, { limit, offset }) => {
      const where: gqlV2.connection_bool_exp = {
        tenantId: { _eq: teamId },
        payments: {},
        app: {
          id: { _neq: 'finalytic' },
        },
        status: whereConnectionStatusDefault,
        _or: search
          ? [
              {
                name: { _ilike: `%${search}%` },
              },
            ]
          : undefined,
      };

      const list = q
        .connection({
          where,
          order_by: [{ name: 'asc' }],
          limit,
          offset,
        })
        .map<SelectItem>((res) => ({
          value: res.id,
          label: res.name || 'Missing connection name',
        }));

      const aggregate =
        q.connectionAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchOptions,
      queryKey: ['payments', 'connections'],
      variables: {
        teamId,
        search: search?.trim(),
      },
    }
  );

  const { data } = useQuery(
    (q, { connectionId }) => {
      if (!connectionId) return { value: null };

      const value = q
        .connection({ where: { id: { _eq: connectionId } } })
        .map<SelectItem>((item) => ({
          label: item.name || '',
          value: item.id,
        }))?.[0];

      return {
        value,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchValue,
      queryKey: ['payments', 'connections'],
      keepPreviousData: true,
      variables: {
        connectionId: filter.connectionId,
      },
    }
  );

  const value = gate.hasValue ? data?.value || null : null;

  return (
    <Filter.Select
      value={value}
      setValue={(v) => setFilter({ connectionId: v?.value || null })}
      label="Connection"
      type="single"
      withinPortal
      onOpenChange={gate.onOpenChange}
      infiniteData={{ ...queryData, setSearch }}
    />
  );
};

const LineTypeFilter = () => {
  const [{ id: teamId }] = useTeam();
  const { filter, setFilter } = usePaymentFilter();
  const lineTypes = filter.lineTypes?.filter(hasValue) ?? [];
  const gate = useSelectQueryGate({ value: lineTypes });

  const { data, isLoading: loading } = useQuery(
    (q, { teamId }) => {
      const where: gqlV2.payment_line_bool_exp = {
        tenantId: { _eq: teamId },
        type2: { _is_null: false },
      };

      const data = q
        .paymentLines({
          where,
          order_by: [{ type2: 'asc' }],
          distinct_on: ['type2'],
        })
        .map<SelectItem>((res) => ({
          value: res.type2!,
          label: res.type2!,
        }));

      return {
        data,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchOptions,
      queryKey: ['payments', 'paymentLines'],
      keepPreviousData: true,
      variables: {
        teamId,
      },
    }
  );

  const value = gate.hasValue
    ? lineTypes.map<SelectItem>((lineType) => ({
        value: lineType,
        label: lineType,
      }))
    : [];

  const options = useMemo(() => {
    return data?.data || [];
  }, [data?.data]);

  return (
    <Filter.Select
      value={value}
      setValue={(v) =>
        setFilter({ lineTypes: v.length ? v.map((i) => i.value) : null })
      }
      label="Line Type"
      type="multiple"
      withinPortal
      onOpenChange={gate.onOpenChange}
      data={{
        options,
        loading,
      }}
      popoverWidth={350}
    />
  );
};

const ListingFilter = () => {
  const [{ id: teamId, isGL: GL }] = useTeam();
  const { filter, setFilter } = usePaymentFilter();
  const [search, setSearch] = useState('');
  const [dashboard] = useDashboard();
  const gate = useSelectQueryGate({ value: filter.listingIds });

  const queryData = useInfiniteQuery(
    (q, { teamId, search, dashboard, GL }, { limit, offset }) => {
      const where: gqlV2.listing_bool_exp = whereListings({
        tenantId: teamId,
        dashboard,
        search,
        GL,
        ownerPortalContactId: null,
      });

      const list = q
        .listings({
          where,
          order_by: [{ calculated_title: 'asc_nulls_last' }],
          limit,
          offset,
        })
        .map<SelectItem>((res) => ({
          value: res.id,
          label: getListingName(res),
        }));

      const aggregate = q.listingAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchOptions,
      queryKey: ['payments', 'listings'],
      variables: {
        teamId,
        search: search?.trim(),
        dashboard,
        GL,
      },
    }
  );
  const { data } = useQuery(
    (q, { listingIds }) => {
      const value = q
        .listings({ where: { id: { _in: listingIds } } })
        .map<SelectItem>((item) => ({
          label: getListingName(item),
          value: item.id,
        }));

      return {
        value,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchValue,
      queryKey: ['payments', 'listings'],
      keepPreviousData: true,
      variables: {
        listingIds: (filter.listingIds || []).filter(hasValue),
      },
    }
  );

  const value = gate.hasValue ? data?.value || [] : [];

  return (
    <Filter.Select
      value={value}
      setValue={(v) =>
        setFilter({ listingIds: v.length ? v.map((i) => i.value) : null })
      }
      label="Listing"
      type="multiple"
      withinPortal
      onOpenChange={gate.onOpenChange}
      infiniteData={{ ...queryData, setSearch }}
    />
  );
};

const ListingGroupFilter = () => {
  const [{ id: teamId }] = useTeam();
  const { filter, setFilter } = usePaymentFilter();
  const gate = useSelectQueryGate({ value: filter.listingCollectionId });

  const [search, setSearch] = useState('');

  const queryData = useInfiniteQuery(
    (q, { teamId, search }, { limit, offset }) => {
      const where: gqlV2.listing_collection_bool_exp = {
        tenantId: { _eq: teamId },
        name: search
          ? { _ilike: `%${search}%` }
          : { _is_null: false, _neq: '' },
      };

      const list = q
        .listingCollections({
          where,
          order_by: [{ name: 'asc_nulls_last' }],
          limit,
          offset,
        })
        .map<SelectItem>((res) => ({
          value: res.id,
          label: res.name || 'No name',
        }));

      const aggregate =
        q.listingCollectionAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchAny,
      queryKey: ['reservations', 'listings'],
      variables: {
        teamId,
        search: search?.trim(),
      },
    }
  );

  const values = queryData.data?.pages.flatMap((page) => page.list) || [];
  const value = gate.hasValue
    ? values.find((v) => v.value === filter.listingCollectionId) || null
    : null;

  return (
    <Filter.Select
      value={value}
      setValue={(v) => setFilter({ listingCollectionId: v?.value || null })}
      label="Listing Group"
      type="single"
      withinPortal
      noOptionsText="No listing groups found"
      onOpenChange={gate.onOpenChange}
      infiniteData={{ ...queryData, setSearch }}
    />
  );
};

const AppFilter = () => {
  const [{ id: teamId }] = useTeam();
  const { filter, setFilter } = usePaymentFilter();
  const gate = useSelectQueryGate({ value: filter.appId });

  const { data, isLoading: loading } = useQuery(
    (q, { teamId }) => {
      const where: gqlV2.connection_bool_exp = {
        tenantId: { _eq: teamId },
        payments: {},
        app: {
          id: { _neq: 'finalytic' },
        },
        appId: { _is_null: false },
        status: whereConnectionStatusDefault,
      };

      const data = q
        .connection({
          where,
          order_by: [{ appId: 'asc' }],
          distinct_on: ['appId'],
        })
        .map<SelectItem>((res) => ({
          value: res.appId!,
          label: res.app.name || '',
        }));

      return {
        data,
      };
    },
    {
      keepPreviousData: true,
      skip: !teamId || !gate.shouldFetchAny,
      queryKey: ['payments', 'apps'],
      variables: {
        teamId,
      },
    }
  );

  const options = useMemo(() => {
    return data?.data || [];
  }, [data?.data]);

  const value = useMemo(() => {
    return gate.hasValue
      ? options.find((i) => i.value === filter.appId) || null
      : null;
  }, [filter.appId, gate.hasValue, options]);

  return (
    <Filter.Select
      value={value}
      setValue={(v) => setFilter({ appId: v?.value || null })}
      label="Integration"
      type="single"
      withinPortal
      onOpenChange={gate.onOpenChange}
      data={{
        options,
        loading,
      }}
    />
  );
};

const AUTOMATION_FILTER_OPTIONS: SelectItem<syncStatus_enum>[] = [
  { label: 'Failed Automations', value: 'failed' },
];

const AutomationFilter = () => {
  const { filter, setFilter } = usePaymentFilter();

  const value = filter.automationResult ? AUTOMATION_FILTER_OPTIONS[0] : null;

  return (
    <Filter.Select
      value={value}
      setValue={(v) => setFilter({ automationResult: v?.value || null })}
      label="Automations"
      type="single"
      withinPortal
      data={{
        options: AUTOMATION_FILTER_OPTIONS,
      }}
    />
  );
};
